import React, { useState, useEffect, useLayoutEffect } from "react";
import { MapContainer, TileLayer, AttributionControl } from "react-leaflet";
import IsLoading from "../../components/IsLoading";
import HasError from "../../components/HasError";
import { useSelector, useDispatch } from "react-redux";
import MyCustomMarker from "../../components/MyCustomMarker";
import { fetchStationsTwelve } from "../../store/stationTwelve";
import { fetchStationsDaily } from "../../store/stationsDaily";
import NoAccessPage from "../NoAccessPage";

const Twelve = () => {
  const [AQIType, setAQIType] = useState("daily");
  const isCompany = useSelector((state) => state.login.isCompany);
  const dispatch = useDispatch();
  useLayoutEffect(() => {
    const fetchData = () => {
      dispatch(fetchStationsDaily());
      dispatch(fetchStationsTwelve());
    };

    fetchData();

    const intervalId = setInterval(fetchData, 1500000); // 1500000 ms = 25 minutes

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [dispatch, isCompany]);

  const stationsTwelve = useSelector((state) => state.stationsTwelve);
  const stationsError = stationsTwelve.stationsError;
  const stationsLoading = stationsTwelve.stationsLoarding;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const twelveSAPA = stationsTwelve.stationsData.SAPA;
  const twelveESPS = stationsTwelve.stationsData.ESPS;
  const twelveOSPM = stationsTwelve.stationsData.OSPM;
  const twelveSATA = stationsTwelve.stationsData.SATA;
  const stationsDaily = useSelector(
    (state) => state.stationsDaily.stationsData
  );

  const GetInformationOfTheDevice = ({ deviceType, deviceId }) => {
    // Find the specific station, or default to an empty object
    const theSpecificStation =
      informationData.find(
        (x) =>
          x.device === String(deviceType) && x.device_id === Number(deviceId)
      ) || {};

    // Return the device information object
    return {
      device_id: theSpecificStation.device_id,
      device: theSpecificStation.device,
      is_kit: theSpecificStation.is_kit,
      is_test: theSpecificStation.is_test,
      is_aqms: theSpecificStation.is_aqms,
      user: theSpecificStation.user,
      province: theSpecificStation.province,
      city: theSpecificStation.city,
      stationName: theSpecificStation.stationName,
      longitude: theSpecificStation.longitude,
      latitude: theSpecificStation.latitude,
      slug: theSpecificStation.slug,
    };
  };

  let modifiedTwelveSAPA = null,
    modifiedTwelveSATA = null,
    modifiedTwelveESPS = null,
    modifiedTwelveOSPM = null;

  // Check if twelveSAPA exists before mapping
  if (twelveSAPA && Array.isArray(twelveSAPA)) {
    modifiedTwelveSAPA = twelveSAPA.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "SAPA",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (twelveSATA && Array.isArray(twelveSATA)) {
    modifiedTwelveSATA = twelveSATA.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "SATA",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (twelveESPS && Array.isArray(twelveESPS)) {
    modifiedTwelveESPS = twelveESPS.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "ESPS",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (twelveOSPM && Array.isArray(twelveOSPM)) {
    modifiedTwelveOSPM = twelveOSPM.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "OSPM",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  // Twelve stations list combining all Twelve modified data
  const modifiedTwelveStationsList = [].concat(
    modifiedTwelveSAPA || [],
    modifiedTwelveSATA || [],
    modifiedTwelveESPS || [],
    modifiedTwelveOSPM || []
  );

  let modifiedDailySAPA = null,
    modifiedDailySATA = null,
    modifiedDailyESPS = null,
    modifiedDailyOSPM = null;

  // Daily checks
  if (stationsDaily.SAPA && Array.isArray(stationsDaily.SAPA)) {
    modifiedDailySAPA = stationsDaily.SAPA.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "SAPA",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (stationsDaily.SATA && Array.isArray(stationsDaily.SATA)) {
    modifiedDailySATA = stationsDaily.SATA.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "SATA",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (stationsDaily.ESPS && Array.isArray(stationsDaily.ESPS)) {
    modifiedDailyESPS = stationsDaily.ESPS.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "ESPS",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  if (stationsDaily.OSPM && Array.isArray(stationsDaily.OSPM)) {
    modifiedDailyOSPM = stationsDaily.OSPM.map((element) => {
      const deviceInfo = GetInformationOfTheDevice({
        informationData: informationData,
        deviceType: "OSPM",
        deviceId: element.device_id,
      });
      return { ...element, ...deviceInfo };
    });
  }

  // Daily stations list combining all daily modified data
  const modifiedDailyStationsList = [].concat(
    modifiedDailySAPA || [],
    modifiedDailySATA || [],
    modifiedDailyESPS || [],
    modifiedDailyOSPM || []
  );

  const [mapZoom, setMapZoom] = useState(6);
  const [mapHeight, setMapHeight] = useState("90vh");

  const getInformationOfStations = () => {
    if (AQIType === "twelve") {
      setTheStations(modifiedTwelveStationsList);
    } else if (AQIType === "daily") {
      setTheStations(modifiedDailyStationsList);
    }
  };

  const [theStations, setTheStations] = useState([]);

  useEffect(() => {
    getInformationOfStations();
  }, [
    AQIType,
    stationsLoading,
    stationsError,
    modifiedTwelveStationsList?.length,
    modifiedDailyStationsList?.length,
    theStations?.length,
    informationLoading,
    informationError,
    isCompany,
  ]);

  if (isCompany && (stationsLoading || informationLoading)) {
    return <IsLoading />;
  } else if (isCompany && (stationsError || informationError)) {
    return <HasError />;
  } else if (isCompany && modifiedTwelveStationsList) {
    return (
      <div className="container my-5">
        <div
          className="bg-secondary rounded-1 p-2"
          style={{ width: "90%", margin: "auto" }}
        >
          <div className="col-md-4">
            <select
              name="average"
              id="average"
              className="form-select w-100 form-select-lg"
              aria-label="Default select example"
              onChange={(e) => setAQIType(e.target.value)}
              value={AQIType}
            >
              <option value="daily">شاخص روزانه</option>
              <option value="twelve">شاخص آنلاین </option>
              {/* <option value="online">شاخص لحظه ای</option> */}
            </select>
          </div>
        </div>
        <MapContainer
          id="payeshMap"
          center={[32.739, 53.688]}
          zoom={mapZoom}
          style={{
            width: "90%",
            height: mapHeight,
            margin: "auto",
          }}
          attributionControl={false}
        >
          {theStations.map((station) => {
            // Skip rendering the marker if the station meets the specified condition
            if (
              (station.device_id === 101 && station.device === "SATA") ||
              station.is_aqms === false
            ) {
              return null; // Use 'null' instead of an empty string for better readability
            }

            return (
              <MyCustomMarker
                key={station.slug}
                theKey={station.slug}
                information={station}
              />
            );
          })}
          <TileLayer
            attribution="ایستگاه های فعال کنترل کیفیت هوا"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AttributionControl position="bottomright" prefix={false} />
        </MapContainer>
      </div>
    );
  } else if (isCompany !== true) {
    return <NoAccessPage />;
  }
};

export default Twelve;
